import React, { useMemo } from "react";
import type { DrugMapping } from "../lib/drugMappings";
import {
  getSpecificationPlaceholder,
  validateSpecification,
} from "../lib/specification";

interface SpecificationInputProps {
  drug: DrugMapping | null;
  dosageForm: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

const SpecificationInput: React.FC<SpecificationInputProps> = ({
  drug,
  dosageForm,
  value,
  onChange,
  className = "",
  disabled = false,
}) => {
  const validation = useMemo(() => validateSpecification(value), [value]);
  const isDisabled = disabled || !drug;

  const handleBlur = () => {
    if (value && validation.isValid && validation.normalized !== value) {
      onChange(validation.normalized);
    }
  };

  return (
    <div className={className}>
      <div className="relative">
        <input
          type="text"
          inputMode="text"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          onBlur={handleBlur}
          disabled={isDisabled}
          maxLength={30}
          aria-label="药品规格"
          aria-invalid={Boolean(value && !validation.isValid)}
          aria-describedby="specification-help"
          placeholder={
            drug
              ? getSpecificationPlaceholder(dosageForm)
              : "请先选择药品"
          }
          className={`w-full h-12 px-4 text-base bg-white dark:bg-gray-800 border rounded-lg
                     outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                     ${
                       value && !validation.isValid
                         ? "border-red-500 focus:ring-2 focus:ring-red-500/30"
                         : "border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     }`}
        />
      </div>

      <div id="specification-help" className="mt-1.5 min-h-5 text-xs">
        {!drug ? (
          <span className="text-gray-500 dark:text-gray-400">
            选择药品后可填写实际规格
          </span>
        ) : value && !validation.isValid ? (
          <span className="text-red-600 dark:text-red-400">
            {validation.error}
          </span>
        ) : value ? (
          <span className="text-green-700 dark:text-green-400">
            将按“{validation.normalized}”进行检索
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">
            可选；请以药品包装或说明书标示为准
          </span>
        )}
      </div>
    </div>
  );
};

export default SpecificationInput;
